import React, { useState, useEffect, useRef } from "react";

const AI_EMBED_URL = "https://myuae-smoky.vercel.app/embed";
const AI_ORIGIN    = "https://myuae-smoky.vercel.app";

export default function FloatingAI() {
  const [isOpen,      setIsOpen]      = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [authSent,    setAuthSent]    = useState(false);
  const iframeRef = useRef(null);


  // ── Get auth from your React app's existing auth system ────────────────
  function getToken() {
    // Your React app likely stores token in localStorage or Redux
    // Check what key your app uses — common patterns:
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("myuae_token") ||
      localStorage.getItem("userToken") ||
      null
    );
  }

  function getUser() {
    try {
      const raw =
        localStorage.getItem("user") ||
        localStorage.getItem("myuae_user") ||
        null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  // ── Send auth token to iframe ───────────────────────────────────────────
  function sendAuth() {
    if (!iframeRef.current?.contentWindow || authSent) return;
    const token = getToken();
    const user  = getUser();

    if (token) {
      iframeRef.current.contentWindow.postMessage(
        {
          type:  "MYUAE_AUTH",
          token,
          user: {
            email:      user?.email      || "",
            firstName:  user?.firstName  || "",
            lastName:   user?.lastName   || "",
            customerId: user?.customerId || user?._id || "",
          },
        },
        AI_ORIGIN
      );
      setAuthSent(true);
    }
  }

  // ── Listen for messages from iframe ────────────────────────────────────
  useEffect(() => {
    function handleMessage(event) {
      if (event.origin !== AI_ORIGIN) return;

      if (event.data?.type === "MYUAE_EMBED_READY") {
        setIframeReady(true);
        sendAuth();
      }
    }

    window.addEventListener("message", handleMessage);

    // Expose logout helper globally
    window.__myuaeAILogout = function () {
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          { type: "MYUAE_LOGOUT" },
          AI_ORIGIN
        );
      }
    };

    return () => {
      window.removeEventListener("message", handleMessage);
      delete window.__myuaeAILogout;
    };
  }, [authSent]);

  // ── Tooltip timer ───────────────────────────────────────────────────────
  useEffect(() => {
    const t1 = setTimeout(() => setShowTooltip(true),  5000);
    const t2 = setTimeout(() => setShowTooltip(false), 12000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  function handleOpen() {
    setIsOpen(true);
    setShowTooltip(false);
    setAuthSent(false); // re-auth on each open
  }

  // ── Styles ──────────────────────────────────────────────────────────────
  const styles = {
      closeBtnWrap: {
        position: "absolute",
        top: "12px",
        right: "12px",
        zIndex: 1000,
      },

      floatingCloseBtn: {
        width: "28px",
        height: "28px",
        borderRadius: "50%",
        border: "none",
        background: "#fff",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,.15)",
        fontSize: "15px",
        fontWeight: 700,
      },
    // Floating button container
    btnWrap: {
      position: "fixed",
      bottom:   "91px",
      right:    "28px",
      zIndex:   950,
    },

    btnWrapClose: {
        position: "fixed",
        bottom:   "91px",
        right:    "28px",
        zIndex:   950,
      },

    // Ping ring
    ping: {
      position:        "absolute",
      inset:           0,
      borderRadius:    "50%",
      backgroundColor: "rgba(59,130,246,0.25)",
      animation:       "myuae-ping 1.8s cubic-bezier(0,0,0.2,1) infinite",
    },

    // The round button
    btn: {
      position:       "relative",
      width:          "62px",
      height:         "62px",
      borderRadius:   "50%",
      background:     "linear-gradient(135deg,#001B8F,#0D5BFF)",
      border:         "none",
      cursor:         "pointer",
      boxShadow:      "0 8px 32px rgba(0,71,255,0.4)",
      display:        "flex",
      alignItems:     "center",
      justifyContent: "center",
      overflow:       "hidden",
      transition:     "transform 0.2s",
    },

    btnImg: {
      width:        "56px",
      height:       "56px",
      borderRadius: "50%",
      objectFit:    "cover",
    },

    closeIcon: {
      color:      "#fff",
      fontSize:   "22px",
      lineHeight: 1,
    },

    // Tooltip bubble
    tooltip: {
      position:        "fixed",
      bottom:          "102px",
      right:           "100px",
      backgroundColor: "#fff",
      color:           "#374151",
      fontSize:        "13px",
      lineHeight:      "1.5",
      padding:         "12px 16px",
      borderRadius:    "16px",
      boxShadow:       "0 4px 24px rgba(0,0,0,0.12)",
      border:          "1px solid #F0EAEA",
      zIndex:          949,
      maxWidth:        "210px",
    },

    tooltipArrow: {
      position:     "absolute",
      right:        "-6px",
      bottom:       "18px",
      width:        0,
      height:       0,
      borderLeft:   "6px solid #fff",
      borderTop:    "6px solid transparent",
      borderBottom: "6px solid transparent",
    },
    chatWindow: {
      position: "fixed",
      bottom: "10px",
      right: "24px",
    
      width: "450px",
      height: "800px",
    
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 120px)",
    
      borderRadius: "24px",
      overflow: "hidden",
    
      background: "#fff",
    
      boxShadow: "0 20px 60px rgba(0,0,0,.18)",
    
      border: "1px solid #E5E7EB",
    
      zIndex: 1200,
    
      display: "flex",
      flexDirection: "column",
    
    },

    // Chat window header
    chatHeader: {
      display:         "flex",
      alignItems:      "center",
      justifyContent:  "space-between",
      padding:         "12px 16px",
      borderBottom:    "1px solid #F3F4F6",
      backgroundColor: "#fff",
      flexShrink:      0,
    },

    headerLeft: {
      display:    "flex",
      alignItems: "center",
      gap:        "10px",
    },

    headerAvatar: {
      width:        "34px",
      height:       "34px",
      borderRadius: "50%",
      objectFit:    "cover",
    },

    headerName: {
      fontSize:   "13px",
      fontWeight: 600,
      color:      "#111827",
      margin:     0,
    },

    headerStatus: {
      fontSize: "11px",
      color:    "#22c55e",
      margin:   0,
    },

    closeBtn: {
      background:   "none",
      border:       "none",
      cursor:       "pointer",
      color:        "#9CA3AF",
      fontSize:     "18px",
      padding:      "4px 8px",
      borderRadius: "8px",
      lineHeight:   1,
    },

    // Loading overlay
    loadingOverlay: {
      position:        "absolute",
      top:             "57px",
      left:            0,
      right:           0,
      bottom:          0,
      display:         "flex",
      flexDirection:   "column",
      alignItems:      "center",
      justifyContent:  "center",
      backgroundColor: "#fff",
      zIndex:          10,
    },

    spinner: {
      width:        "30px",
      height:       "30px",
      border:       "2px solid #3B82F6",
      borderTop:    "2px solid transparent",
      borderRadius: "50%",
      animation:    "myuae-spin 0.8s linear infinite",
      marginBottom: "10px",
    },

    loadingText: {
      fontSize: "13px",
      color:    "#9CA3AF",
    },

    // iframe
    iframe: {
      flex:       1,
      width:      "100%",
      border:     "none",
      display:    "block",
    },
  };

  return (
    <>
      {/* Keyframe animations injected once */}
      <style>{`
        @keyframes myuae-ping {
          75%, 100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes myuae-spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 480px) {
          .myuae-ai-launcher {
            right: 16px !important;
            bottom: calc(96px + env(safe-area-inset-bottom)) !important;
          }
          .myuae-ai-tooltip {
            right: 88px !important;
            bottom: calc(104px + env(safe-area-inset-bottom)) !important;
          }
          .myuae-chat-window {
            bottom: 0 !important;
            right:  0 !important;
            width:  100vw !important;
            height: 100vh !important;
            max-width:  100vw !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
          }
        }
      `}</style>

      {/* ── Chat Window ──────────────────────────────────────────────── */}

{isOpen && (
  <div style={styles.chatWindow} className="myuae-chat-window">

    <div style={styles.closeBtnWrap}>
      <button
        style={styles.floatingCloseBtn}
        onClick={() => setIsOpen(false)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#ff0000";
          e.currentTarget.style.color = "#fff";
          e.currentTarget.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#fff";
          e.currentTarget.style.color = "#111827";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ✕
      </button>
    </div>

    {!iframeReady && (
            <div style={styles.loadingOverlay}>
              <div style={styles.spinner} />
              <p style={styles.loadingText}>Loading My AI Assistant...</p>
            </div>
          )}

          {/* iframe */}
          <iframe
            ref={iframeRef}
            src={AI_EMBED_URL}
            title="My AI Assistant"
            onLoad={() => {
              setIframeReady(true);
              // Send auth after iframe loads
              setTimeout(sendAuth, 300);
            }}
            style={{
              ...styles.iframe,
              visibility: iframeReady ? "visible" : "hidden",
            }}
            allow="clipboard-write"
          />

  </div>
)}

      {/* ── Tooltip ──────────────────────────────────────────────────── */}
      {showTooltip && !isOpen && (
        <div style={styles.tooltip} className="myuae-ai-tooltip">
          👋 Hi! I'm your AI Assistance. Let me help you find the perfect UAE business package.
          <div style={styles.tooltipArrow} />
        </div>
      )}

      {/* ── Floating Button ──────────────────────────────────────────── */}

          {!isOpen && (
            <div style={styles.btnWrap} className="myuae-ai-launcher">
              <span style={styles.ping} />

              <button
                style={styles.btn}
                onClick={handleOpen}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <img
                  src={`${AI_ORIGIN}/myai.png`}
                  alt="Ayesha AI"
                  style={styles.btnImg}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.parentElement.innerHTML = "🤖";
                  }}
                />
              </button>
            </div>
          )}
          
    </>
  );
}
